import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import { posApi, reportsApi } from '@/services/api';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import ReportDateRangeFilter from '@/components/reports/ReportDateRangeFilter';
import ReportExportButton from '@/components/reports/ReportExportButton';
import Pagination, { LIST_PAGE_SIZE } from '@/components/common/Pagination';
import ItemServiceSelect from '@/components/common/ItemServiceSelect';
import { downloadCsv } from '@/utils/csv';
import { formatMoney } from '@/utils/currency';

export default function ServicesAnalysisSection() {
  const {
    dateRange,
    setDateRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    from_date,
    to_date,
  } = useReportDateRange('This Month');

  const [category, setCategory] = useState('');
  const [service, setService] = useState('');
  const [page, setPage] = useState(0);

  const { data: categoriesRes } = useQuery({
    queryKey: ['serviceCategoriesForServicesAnalysis'],
    queryFn: async () => {
      const res = await posApi.getCategories({ category_type: 'Service' });
      return res.data.message;
    },
  });

  const categories: { name: string; category_name: string }[] = categoriesRes || [];

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['servicesAnalysis', from_date, to_date, category, service],
    queryFn: () =>
      reportsApi.getServicesAnalysis({
        from_date,
        to_date,
        category: category || undefined,
        service: service || undefined,
      }),
  });

  useEffect(() => {
    setPage(0);
  }, [rows, from_date, to_date, category, service]);

  const handleExport = () => {
    if (!rows.length) return;
    downloadCsv(
      `services-analysis-${from_date}-${to_date}.csv`,
      [
        { key: 'service_name', label: 'Service' },
        { key: 'revenue', label: 'Revenue' },
        { key: 'avg_price', label: 'Average Price' },
        { key: 'discount', label: 'Discount' },
        { key: 'net_revenue', label: 'Net Revenue' },
      ],
      rows as Record<string, unknown>[]
    );
  };

  const pageRows = rows.slice(page * LIST_PAGE_SIZE, (page + 1) * LIST_PAGE_SIZE);

  return (
    <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/60 border border-slate-100 overflow-hidden">
      <div className="p-5 border-b border-slate-100 space-y-4">
        <div className="flex flex-wrap gap-4 items-end">
          <ReportDateRangeFilter
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            customFrom={customFrom}
            customTo={customTo}
            onCustomFromChange={setCustomFrom}
            onCustomToChange={setCustomTo}
          />

          <div className="space-y-1.5 flex-1 min-w-[160px]">
            <label className="text-sm font-medium text-gray-700">Service Category</label>
            <select className="input-field" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">All Categories</option>
              {categories.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.category_name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5 flex-1 min-w-[200px]">
            <label className="text-sm font-medium text-gray-700">Service</label>
            <ItemServiceSelect
              type="Service"
              value={service}
              onChange={setService}
              placeholder="Search services..."
            />
          </div>
          <ReportExportButton onClick={handleExport} disabled={!rows.length} />
        </div>
      </div>

      <div className="overflow-x-auto max-h-[420px]">
        {isLoading ? (
          <div className="p-8 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : rows.length > 0 ? (
          <table className="w-full text-sm text-left min-w-[640px]">
            <thead className="text-xs font-bold text-[#1fa8ff] bg-white uppercase sticky top-0 border-b border-gray-100">
              <tr>
                <th className="px-4 py-3 font-medium">Service</th>
                <th className="px-4 py-3 font-medium text-right">Revenue</th>
                <th className="px-4 py-3 font-medium text-right">Average Price</th>
                <th className="px-4 py-3 font-medium text-right">Discount</th>
                <th className="px-4 py-3 font-medium text-right">Net Revenue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pageRows.map((row: any) => (
                <tr key={row.service} className="hover:bg-gray-50/50">
                  <td className="px-4 py-3 text-gray-900">{row.service_name}</td>
                  <td className="px-4 py-3 text-right">{formatMoney(row.revenue)}</td>
                  <td className="px-4 py-3 text-right text-gray-500">{formatMoney(row.avg_price)}</td>
                  <td className="px-4 py-3 text-right text-gray-500">{formatMoney(row.discount)}</td>
                  <td className="px-4 py-3 text-right font-medium">{formatMoney(row.net_revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-12 text-center">
            <FileText className="w-8 h-8 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No service analysis data for the selected filters</p>
          </div>
        )}
      </div>

      {rows.length > 0 && (
        <div className="p-4 border-t border-gray-100 bg-gray-50">
          <Pagination page={page} pageSize={LIST_PAGE_SIZE} total={rows.length} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}
